import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface ContactMethod {
  id: string;
  icon: string;
  title: string;
  description: string;
  action: string;
  actionLabel: string;
  availability: string;
  responseTime: string;
}

interface ContactMethodsProps {
  onMethodSelect: (methodId: string) => void;
  className?: string;
}

const ContactMethods = ({ onMethodSelect, className = '' }: ContactMethodsProps) => {
  const contactMethods: ContactMethod[] = [
    {
      id: 'demo',
      icon: 'VideoCameraIcon',
      title: 'Demo Boeken',
      description: 'Plan een persoonlijke demonstratie van onze AI-oplossingen met een expert.',
      action: 'demo',
      actionLabel: 'Boek een demo',
      availability: 'Ma-Vr: 09:00-18:00',
      responseTime: 'Direct bevestiging',
    },
    {
      id: 'consultation',
      icon: 'UserGroupIcon',
      title: 'Gratis Consultatie',
      description: 'Bespreek uw AI-behoeften met onze consultants en ontvang een op maat gemaakt advies.',
      action: 'consultation',
      actionLabel: 'Plan consultatie',
      availability: 'Ma-Vr: 08:00-20:00',
      responseTime: '< 2 uur reactie',
    },
    {
      id: 'chat',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'Live Chat',
      description: 'Chat direct met onze AI-assistent of word doorverbonden met een specialist.',
      action: 'chat',
      actionLabel: 'Start chat',
      availability: '24/7 beschikbaar',
      responseTime: 'Onmiddellijk',
    },
    {
      id: 'phone',
      icon: 'PhoneIcon',
      title: 'Telefonisch Contact',
      description: 'Bel ons direct voor urgente vragen of technische ondersteuning.',
      action: 'phone',
      actionLabel: 'Bel nu',
      availability: 'Ma-Vr: 08:00-20:00',
      responseTime: 'Direct contact',
    },
    {
      id: 'email',
      icon: 'EnvelopeIcon',
      title: 'E-mail Contact',
      description: 'Stuur ons een gedetailleerde vraag en ontvang een uitgebreid antwoord.',
      action: 'email',
      actionLabel: 'Stuur e-mail',
      availability: 'Altijd open',
      responseTime: '< 4 uur reactie',
    },
    {
      id: 'office',
      icon: 'BuildingOfficeIcon',
      title: 'Kantoorbezoek',
      description: 'Bezoek een van onze kantoren in Nederland of België voor een persoonlijk gesprek.',
      action: 'office',
      actionLabel: 'Plan bezoek',
      availability: 'Op afspraak',
      responseTime: 'Binnen 24 uur',
    },
  ];

  return (
    <section className={`py-16 lg:py-24 ${className}`}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground mb-4">
            Kies uw voorkeurscontactmethode
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            We bieden verschillende manieren om contact op te nemen, zodat u kunt kiezen wat het beste bij u past.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contactMethods.map((method) => (
            <div
              key={method.id}
              className="glassmorphic rounded-2xl p-6 hover:glow-primary transition-all duration-300 group cursor-pointer"
              onClick={() => onMethodSelect(method.id)}
            >
              <div className="flex items-start space-x-4 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors duration-300">
                  <Icon name={method.icon as any} size={24} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-foreground mb-2">{method.title}</h3>
                  <p className="text-sm text-muted-foreground">{method.description}</p>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="ClockIcon" size={16} className="text-accent" />
                  <span className="text-muted-foreground">{method.availability}</span>
                </div>
                <div className="flex items-center space-x-2 text-sm">
                  <Icon name="BoltIcon" size={16} className="text-success" />
                  <span className="text-muted-foreground">{method.responseTime}</span>
                </div>
              </div>

              <button className="w-full px-4 py-3 rounded-lg bg-primary/10 text-primary font-semibold hover:bg-primary hover:text-primary-foreground transition-all duration-300 group-hover:shadow-premium">
                {method.actionLabel}
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ContactMethods;